class AvailabilityService {
  constructor() {
    this.cache = new Map();
    this.pendingRequests = new Map();
  }

  getBaseUrl() {
    return (
      window.AVAILABILITY_CONFIG?.apiUrl ||
      "https://production-api-761814967974.australia-southeast1.run.app/ideal-electrical-nz/inventory/availability"
    );
  }

  async fetchAvailability(skus, summaryOnly = true) {
    // Check if account number is available
    if (!window.AVAILABILITY_CONFIG || !window.AVAILABILITY_CONFIG.accountNumber) {
      console.warn("[AvailabilityService] No account number available - cannot fetch availability");
      return {};
    }

    // Convert single SKU to array
    const skuArray = Array.isArray(skus) ? skus : [skus];

    // Filter out SKUs we already have cached
    const uncachedSkus = skuArray.filter(
      (sku) => !this.cache.has(`${sku}|summaryOnly=${summaryOnly}`)
    );

    if (uncachedSkus.length === 0) {
      // Return cached data for all requested SKUs
      return skuArray.reduce((acc, sku) => {
        acc[sku] = this.cache.get(`${sku}|summaryOnly=${summaryOnly}`);
        return acc;
      }, {});
    }

    // Check if there's already a pending request for these SKUs and summaryOnly
    const cacheKey = uncachedSkus.sort().join(",") + `|summaryOnly=${summaryOnly}`;
    if (this.pendingRequests.has(cacheKey)) {
      return this.pendingRequests.get(cacheKey);
    }

    // Create new request
    const accountNumber = window.AVAILABILITY_CONFIG.accountNumber;

    const params = new URLSearchParams();
    uncachedSkus.forEach((sku) => params.append("skus[]", sku));
    params.append("accountNumber", accountNumber);
    params.append("summaryOnly", summaryOnly);

    const apiUrl = `${this.getBaseUrl()}?${params.toString()}`;

    const request = fetch(apiUrl, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    }).then(async (response) => {
      const data = await response.json();

      // Cache the results
      Object.entries(data).forEach(([sku, availability]) => {
        this.cache.set(`${sku}|summaryOnly=${summaryOnly}`, availability);
      });

      // Remove from pending requests
      this.pendingRequests.delete(cacheKey);

      // Return data for all requested SKUs (including cached ones)
      const result = skuArray.reduce((acc, sku) => {
        acc[sku] = this.cache.get(`${sku}|summaryOnly=${summaryOnly}`);
        return acc;
      }, {});

      return result;
    });

    // Store pending request
    this.pendingRequests.set(cacheKey, request);

    return request;
  }

  // Helper method to get stock class based on quantity
  getStockClass(stock) {
    if (stock < 5) return "out-of-stock";
    if (stock < 10) return "low-stock";
    return "in-stock";
  }

  // Helper method to format availability text
  formatAvailabilityText(quantity, label, type) {
    return `${quantity} ${label || `available for ${type}`}`;
  }

  updateQuantityAndLabel(quantityElement, labelElement, quantity, label, type) {
    if (quantityElement) {
      quantityElement.textContent = quantity;
      quantityElement.classList.remove("hide-on-desktop");
    }

    if (labelElement) {
      if (quantity === 0) {
        labelElement.textContent = "We can order it for you";
      } else {
        labelElement.textContent = ` ${label || `available for ${type}`}`;
      }
    }
  }

  // Update availability display for a single element
  updateAvailabilityDisplay(element, data) {
    if (!data || !data.summary) {
      this.showAvailabilityError(element);
      return;
    }

    const collection = data.summary.collection;
    const delivery = data.summary.delivery;

    const dispatchQuantity = element.querySelector("[data-dispatch-quantity]");
    const dispatchLabel = element.querySelector("[data-dispatch-label]");
    const collectionQuantity = element.querySelector("[data-collection-quantity]");
    const collectionLabel = element.querySelector("[data-collection-label]");
    const dispatchIcon = element.querySelector("[data-dispatch-icon]");
    const collectionIcon = element.querySelector("[data-collection-icon]");

    // Update collection text and icon
    if (collectionQuantity || collectionLabel) {
      if (!collection) {
        if (collectionQuantity) collectionQuantity.textContent = "N/A";
        if (collectionLabel) collectionLabel.textContent = "";
      } else {
        this.updateQuantityAndLabel(
          collectionQuantity,
          collectionLabel,
          collection.qty,
          collection.label,
          "collection"
        );
      }
    }

    // Update collection icon color based on quantity
    if (collectionIcon) {
      if (collection && collection.qty === 0) {
        collectionIcon.classList.add("out-of-stock");
      } else {
        collectionIcon.classList.remove("out-of-stock");
      }
    }

    // Update delivery text and icon
    if (dispatchQuantity || dispatchLabel) {
      if (!delivery) {
        if (dispatchQuantity) dispatchQuantity.textContent = "N/A";
        if (dispatchLabel) dispatchLabel.textContent = "";
      } else {
        this.updateQuantityAndLabel(
          dispatchQuantity,
          dispatchLabel,
          delivery.qty,
          delivery.label,
          "delivery"
        );
      }
    }

    // Update dispatch icon color based on quantity
    if (dispatchIcon) {
      if (delivery && delivery.qty === 0) {
        dispatchIcon.classList.add("out-of-stock");
      } else {
        dispatchIcon.classList.remove("out-of-stock");
      }
    }

    // Store the full data for the modal
    element.dataset.stockData = JSON.stringify(data);
  }

  // Show error state
  showAvailabilityError(element) {
    const dispatchQuantity = element.querySelector("[data-dispatch-quantity]");
    const dispatchLabel = element.querySelector("[data-dispatch-label]");
    const collectionQuantity = element.querySelector("[data-collection-quantity]");
    const collectionLabel = element.querySelector("[data-collection-label]");

    if (collectionQuantity) {
      collectionQuantity.textContent = "Error";
    }
    if (collectionLabel) {
      collectionLabel.textContent = "";
    }
    if (dispatchQuantity) {
      dispatchQuantity.textContent = "Error";
    }
    if (dispatchLabel) {
      dispatchLabel.textContent = "";
    }
  }

  // Show loading state
  showAvailabilityLoading(element) {
    const dispatchQuantity = element.querySelector("[data-dispatch-quantity]");
    const dispatchLabel = element.querySelector("[data-dispatch-label]");
    const collectionQuantity = element.querySelector("[data-collection-quantity]");
    const collectionLabel = element.querySelector("[data-collection-label]");

    if (collectionQuantity) {
      collectionQuantity.textContent = "Loading...";
    }
    if (collectionLabel) {
      collectionLabel.textContent = "";
    }
    if (dispatchQuantity) {
      dispatchQuantity.textContent = "Loading...";
    }
    if (dispatchLabel) {
      dispatchLabel.textContent = "";
    }
  }

  // Handle SearchSpring search results by adding web components
  handleSearchSpringResults() {
    console.log("[AvailabilityService] handleSearchSpringResults() called");

    // Find all .ss__result containers
    const ssResultsContainers = document.querySelectorAll(".ss__result");
    console.log(
      `[AvailabilityService] Found ${ssResultsContainers.length} SearchSpring result containers`
    );

    if (ssResultsContainers.length === 0) {
      console.log("[AvailabilityService] No SearchSpring result containers found, exiting");
      return;
    }

    let totalProcessed = 0;
    let totalSkipped = 0;
    let totalInjected = 0;

    // Process each container
    ssResultsContainers.forEach((container, containerIndex) => {
      console.log(
        `[AvailabilityService] Processing container ${containerIndex + 1}/${
          ssResultsContainers.length
        }`
      );

      // Find all .productitem elements within this container
      const productItems = container.querySelectorAll(".productitem");
      console.log(
        `[AvailabilityService] Found ${productItems.length} product items in container ${
          containerIndex + 1
        }`
      );

      productItems.forEach((productItem, itemIndex) => {
        totalProcessed++;
        console.log(
          `[AvailabilityService] Processing product item ${itemIndex + 1}/${
            productItems.length
          } in container ${containerIndex + 1}`
        );

        // Check if we already added a web component to this item
        const existingComponent = productItem.querySelector("product-availability");
        if (existingComponent) {
          console.log(
            `[AvailabilityService] Product item ${
              itemIndex + 1
            } already has availability component, checking for and removing search spring product-availability div`
          );
          const existingAvailabilityDiv = productItem.querySelector(".product-availability");
          if (existingAvailabilityDiv) {
            existingAvailabilityDiv.remove();
          }
          totalSkipped++;
          return;
        }

        // Extract SKU from the productitem--ideal-part-value element
        const skuElement = productItem.querySelector(".productitem--ideal-part-value");
        if (!skuElement) {
          console.warn(
            `[AvailabilityService] No SKU element found in product item ${itemIndex + 1}, skipping`
          );
          totalSkipped++;
          return;
        }

        const sku = skuElement.textContent.trim();
        if (!sku) {
          console.warn(
            `[AvailabilityService] Empty SKU found in product item ${itemIndex + 1}, skipping`
          );
          totalSkipped++;
          return;
        }

        console.log(`[AvailabilityService] Creating availability component for SKU: ${sku}`);

        // Create the product-availability web component
        const availabilityComponent = document.createElement("product-availability");
        availabilityComponent.setAttribute("sku", sku);
        availabilityComponent.setAttribute("show-dispatch", "true");
        availabilityComponent.setAttribute("show-collection", "true");
        availabilityComponent.setAttribute("show-branch-button", "true");
        availabilityComponent.setAttribute("context", "grid");

        console.log(`[AvailabilityService] Created availability component with attributes:`, {
          sku: sku,
          "show-dispatch": "true",
          "show-collection": "true",
          "show-branch-button": "true",
          context: "grid",
        });

        // Find and replace the existing SearchSpring .product-availability div
        const existingAvailabilityDiv = productItem.querySelector(".product-availability");

        if (existingAvailabilityDiv) {
          console.log(
            `[AvailabilityService] Found existing .product-availability div, replacing with web component`
          );
          // Replace the existing div with our web component
          existingAvailabilityDiv.parentNode.replaceChild(
            availabilityComponent,
            existingAvailabilityDiv
          );
          console.log(
            `[AvailabilityService] Successfully replaced existing div with web component for SKU: ${sku}`
          );
          totalInjected++;
        } else {
          console.log(
            `[AvailabilityService] No existing .product-availability div found, looking for insertion point`
          );

          // Fallback: try to find info container and append there
          const infoContainer = productItem.querySelector(".productitem--info");

          if (infoContainer) {
            console.log(`[AvailabilityService] Found .productitem--info container`);

            // Insert after part numbers if they exist, otherwise append to info container
            const partNumbers = infoContainer.querySelector(".productitem--part-numbers");

            if (partNumbers) {
              console.log(
                `[AvailabilityService] Found .productitem--part-numbers, inserting after it`
              );
              partNumbers.insertAdjacentElement("afterend", availabilityComponent);
              console.log(
                `[AvailabilityService] Successfully inserted component after part numbers for SKU: ${sku}`
              );
            } else {
              console.log(
                `[AvailabilityService] No part numbers found, appending to info container`
              );
              infoContainer.appendChild(availabilityComponent);
              console.log(
                `[AvailabilityService] Successfully appended component to info container for SKU: ${sku}`
              );
            }
            totalInjected++;
          } else {
            console.log(
              `[AvailabilityService] No .productitem--info found, appending directly to product item`
            );
            // Final fallback: append directly to the productitem
            productItem.appendChild(availabilityComponent);
            console.log(
              `[AvailabilityService] Successfully appended component to product item for SKU: ${sku}`
            );
            totalInjected++;
          }
        }
      });
    });

    console.log(`[AvailabilityService] SearchSpring injection complete. Summary:`, {
      totalProcessed,
      totalSkipped,
      totalInjected,
      containers: ssResultsContainers.length,
    });
  }
}

// Make the class available globally
window.AvailabilityService = AvailabilityService;

// Create singleton instance and attach to window for backward compatibility
window.availabilityService = new AvailabilityService();

// Initialize availability displays on all product pages (legacy support)
document.addEventListener("DOMContentLoaded", async () => {
  const elements = document.querySelectorAll("[data-product-availability]");
  if (elements.length === 0) {
    return;
  }
  const skus = Array.from(elements).map((el) => el.dataset.sku);
  // Show loading state for all elements
  elements.forEach((el) => window.availabilityService.showAvailabilityLoading(el));
  try {
    const data = await window.availabilityService.fetchAvailability(skus);
    elements.forEach((el) => {
      const sku = el.dataset.sku;
      if (data[sku]) {
        window.availabilityService.updateAvailabilityDisplay(el, data[sku]);
      } else {
        window.availabilityService.showAvailabilityError(el);
      }
    });
  } catch (error) {
    elements.forEach((el) => window.availabilityService.showAvailabilityError(el));
  }
});

// Handle SearchSpring search results with MutationObserver
let searchSpringObserver = null;

function setupSearchSpringObserver() {
  // If observer already exists, disconnect it
  if (searchSpringObserver) {
    searchSpringObserver.disconnect();
  }

  // Create a new MutationObserver to watch for SearchSpring results
  searchSpringObserver = new MutationObserver((mutations) => {
    let shouldProcess = false;

    mutations.forEach((mutation) => {
      // Check if any added nodes contain SearchSpring results
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) {
          // Check if this node or its children contain SearchSpring results
          if (
            node.classList?.contains("ss__results") ||
            node.querySelector?.(".ss__results") ||
            node.classList?.contains("ss__result") ||
            node.querySelector?.(".ss__result")
          ) {
            shouldProcess = true;
          }
        }
      });
    });

    if (shouldProcess) {
      console.log("[AvailabilityService] SearchSpring results detected in DOM, processing...");
      // Disconnect observer to avoid multiple triggers
      searchSpringObserver.disconnect();
      searchSpringObserver = null;
      // Process the results
      window.availabilityService.handleSearchSpringResults();
    }
  });

  // Start observing the document body for changes
  searchSpringObserver.observe(document.body, {
    childList: true,
    subtree: true,
  });

  console.log("[AvailabilityService] SearchSpring MutationObserver set up");
}

// Handle SearchSpring search results
window.addEventListener("searchspring.loaded", () => {
  console.log("[AvailabilityService] searchspring.loaded event fired, setting up MutationObserver");

  // Set up the observer to watch for when results are actually added to DOM
  setupSearchSpringObserver();

  // Also try immediate processing as a fallback
  setTimeout(() => {
    const ssResults = document.querySelectorAll(".ss__results, .ss__result");
    if (ssResults.length > 0) {
      console.log("[AvailabilityService] Fallback: Found SearchSpring results after timeout");
      if (searchSpringObserver) {
        searchSpringObserver.disconnect();
        searchSpringObserver = null;
      }
      window.availabilityService.handleSearchSpringResults();
    }
  }, 1000);
});
